import { Menu, MessageSquare } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NotificationBell } from "./NotificationBell";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useSession } from "../lib/auth";
import { useLogout } from "../lib/auth";
import { ROLE_LABEL } from "../lib/permissions";
import { cn } from "../lib/cn";

interface HeaderProps {
  onOpenNav: () => void;
  onOpenChat: () => void;
}

export function Header({ onOpenNav, onOpenChat }: HeaderProps): JSX.Element {
  const { user } = useSession();
  const initial = user?.name?.trim()?.[0]?.toUpperCase() ?? "?";
  const navigate = useNavigate();
  const logout = useLogout();
  const { t } = useTranslation(["common", "nav"]);

  return (
    <header
      className={cn(
        "sticky top-0 z-30 h-14 bg-surface border-b border-border",
        "flex items-center gap-2 px-3 sm:px-4",
      )}
    >
      {/* Mobile hamburger — proto .iconbtn style */}
      <button
        type="button"
        onClick={onOpenNav}
        aria-label="Open navigation"
        className={cn(
          "lg:hidden w-9 h-9 rounded-[8px] border border-border bg-surface",
          "flex items-center justify-center text-muted",
          "hover:bg-surface-2 transition-colors",
          // Touch target
          "min-h-[44px] min-w-[44px] sm:min-h-[36px] sm:min-w-[36px]",
        )}
      >
        <Menu className="h-4 w-4" aria-hidden />
      </button>

      {/* Brand — real Hightekers logo (symbol on mobile, wordmark + "Corporate Map" on sm+) */}
      <Link
        to="/"
        className="flex items-center gap-2.5 mr-2 sm:mr-4 shrink-0"
        aria-label="Hightekers Corporate Map — home"
      >
        {/* Mobile: compact symbol mark only */}
        <img
          src="/hightekers-symbol.svg"
          alt="Hightekers"
          className="h-7 w-7 sm:hidden shrink-0"
        />
        {/* sm+: full wordmark lockup + subordinate "Corporate Map" label */}
        <img
          src="/hightekers-wordmark.svg"
          alt="Hightekers"
          className="hidden sm:block h-[22px] w-auto shrink-0"
        />
        <span aria-hidden className="hidden sm:block h-5 w-px bg-border-2" />
        <span className="hidden sm:block text-[11px] font-medium tracking-[.04em] uppercase text-text leading-none">
          Corporate Map
        </span>
      </Link>

      {/* Spacer — pushes bell + user menu to the right on ALL viewports */}
      <div className="flex-1" />

      {/* Mobile chat button — proto .iconbtn */}
      <button
        type="button"
        onClick={onOpenChat}
        aria-label="Open AI assistant"
        className={cn(
          "lg:hidden w-9 h-9 rounded-[8px] border border-border bg-surface",
          "flex items-center justify-center text-muted",
          "hover:bg-surface-2 transition-colors",
          "min-h-[44px] min-w-[44px] sm:min-h-[36px] sm:min-w-[36px]",
        )}
      >
        <MessageSquare className="h-4 w-4" aria-hidden />
      </button>

      {/* Notification bell — stays TOP-RIGHT, visible on ALL viewports */}
      <NotificationBell />

      {/* Desktop user menu — stays TOP-RIGHT */}
      <div className="hidden md:flex items-center gap-2 ml-1 shrink-0">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className={cn(
                "flex items-center gap-2 h-9 px-3 rounded-lg border border-border bg-surface",
                "hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-accent/20",
                "transition-colors",
              )}
              aria-label="User menu"
            >
              {/* Proto .avatar */}
              <span
                aria-hidden
                className="w-7 h-7 rounded-full bg-[#1c1b2e] text-white text-[11.5px] font-semibold flex items-center justify-center shrink-0"
              >
                {initial}
              </span>
              <span className="hidden xl:inline text-[13px] text-text max-w-[160px] truncate">
                {user?.name ?? "—"}
              </span>
              <span
                className="text-[10px] text-muted bg-surface-2 border border-border px-1.5 py-0.5 rounded"
                aria-label={`Role: ${user ? ROLE_LABEL[user.role] : "none"}`}
              >
                {user ? ROLE_LABEL[user.role] : "—"}
              </span>
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              sideOffset={6}
              align="end"
              className="z-50 min-w-[160px] bg-surface border border-border rounded-lg shadow-pop p-1 animate-in fade-in-0 zoom-in-95"
            >
              <DropdownMenu.Item
                onSelect={() => navigate("/settings")}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-[13px] text-text cursor-pointer hover:bg-surface-2 focus:bg-surface-2 focus:outline-none min-h-[36px]"
              >
                {t("nav:settings")}
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="h-px bg-border my-1" />
              <DropdownMenu.Item
                onSelect={() => void logout()}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-[13px] text-red-text cursor-pointer hover:bg-red-soft focus:bg-red-soft focus:outline-none min-h-[36px]"
              >
                {t("common:sign_out")}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      {/* Mobile avatar (no menu — sign-out accessible via sidebar settings link) */}
      <span
        aria-hidden
        className="md:hidden w-9 h-9 rounded-full bg-[#1c1b2e] text-white text-sm font-semibold flex items-center justify-center shrink-0"
        title={user?.name ?? ""}
      >
        {initial}
      </span>
    </header>
  );
}
